import { AxiosResponse } from "axios";
import { createRef, useContext, useEffect, useState } from "react";
import { AdminFrame } from "views/common/AdminFrame";
import { InputDate } from "views/common/InputDate";
import { getApiDataWithToken, postApiDataWithToken } from "views/utils/CallApi";
import { CtxAuth } from "views/utils/Contexts";
import * as mb from "../../../common/MessageBox";
import { getRef, setRef } from "../../../utils/InputHelp";
import * as Validation from "../../../utils/Validation";

interface rowData {
    datetime_start: string,
    datetime_end: string,
    tax: string,
}
interface rowRef {
    datetime_start: React.RefObject<HTMLInputElement>,
    datetime_end: React.RefObject<HTMLInputElement>,
    tax: React.RefObject<HTMLInputElement>,
}

export function Tax(){

    const cua = useContext(CtxAuth);
    const [msgArgs, setMsgArgs] = useState({type:"", text:""});
    const [rowData, setRowData] = useState<rowData[]>([]);      //行の値保持用
    const [rowRef, setRowRef] = useState<rowRef[]>([]);         //行のコントロール保持用
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);

    //-------------------------------------
    // データ取得
    //-------------------------------------
    useEffect(function(){

        let apiRes:any = [];

        getApiDataWithToken('/management/gettings/tax',
            {},
            cua.getToken(),
            function(res:AxiosResponse<any>){
                switch (res.status){
                    case 200:
                        
                        apiRes = res.data.tax_list;
                        
                        let tmpRowData:rowData[] = [];
                        let tmpRowRef:rowRef[] = [];
                        apiRes.forEach(function(item:any){
                            // //入力項目用Refの生成
                            tmpRowRef.push({datetime_start: createRef(), datetime_end: createRef(), tax: createRef()});
                            //データの保持
                            tmpRowData.push({datetime_start: item.datetime_start, datetime_end: item.datetime_end, tax: item.tax});
                        });
                        setRowRef(tmpRowRef);
                        setRowData(tmpRowData);
                        setSubmitDisabled(false);
                        break;
                    default:
                        setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"画面のロードに失敗しました。"});
                }
            }
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //-------------------------------------
    // データ設定
    //-------------------------------------
    //1度目のレンダリングの後の処理で生成される入力項目 に値を設定するには
    //2回目のレンダリングされた後に処理する必要がある
    useEffect(function(){
        rowData.forEach(function(item, idx){
            setRef(rowRef[idx].datetime_start, item.datetime_start);
            setRef(rowRef[idx].datetime_end, item.datetime_end);
            setRef(rowRef[idx].tax, item.tax);
        });
    }, [rowData, rowRef]);

    //-------------------------------------
    // 行追加
    //-------------------------------------
    function clickAddRow(){
        setRowData([...getCurrentRowData(), {datetime_start: "", datetime_end: "", tax: ""}]);
        setRowRef([...rowRef, {datetime_start: createRef(), datetime_end: createRef(), tax: createRef()}]);
    }

    //-------------------------------------
    // 行削除
    //-------------------------------------
    function clickDelRow(idx:number){
        let tmpRowData:rowData[] = getCurrentRowData();
        let tmpRowRef:rowRef[] = rowRef.concat();
        tmpRowData.splice(idx, 1);
        tmpRowRef.splice(idx, 1);
        setRowData(tmpRowData);
        setRowRef(tmpRowRef);
    }

    //-------------------------------------
    // 画面上データの取得
    //-------------------------------------
    function getCurrentRowData():rowData[]{
        let tmpRowData:rowData[] = [];
        rowRef.forEach(function(item){
            tmpRowData.push({datetime_start: getRef(item.datetime_start), datetime_end: getRef(item.datetime_end), tax: getRef(item.tax)});
        });
        return tmpRowData;
    }
    
    //-------------------------------------
    // 変更を保存
    //-------------------------------------
    function clickSave(){

        setSubmitDisabled(true);

        let tmpRowData:rowData[] = getCurrentRowData();

        let errmsg:string = "";
        setMsgArgs({type:mb.MSGBOX_TYPE_INFO, text:""});

        //バリデーションチェック：各行
        let before_edate:any = null;
        tmpRowData.some(function(item, idx){
    
            //開始日・適用日
            errmsg = Validation.checkEmptyTextBox(item.datetime_start, "適用開始日")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkEmptyTextBox(item.datetime_end, "適用終了日")
            if ((idx < rowRef.length - 1) && (errmsg)){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg + "最終行以外の適用終了日は入力必須です。"});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkReversalDate(new Date(item.datetime_start), new Date(item.datetime_end), "適用開始日", "適用終了日")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkReversalDateRow(new Date(item.datetime_start), before_edate, "適用開始日", "適用終了日")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            
            //消費税
            errmsg = Validation.checkEmptyTextBox(item.tax, "消費税（%）")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkNumber(item.tax, "消費税（%）")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkAfterTheDecimalPoint(item.tax, 2, "消費税（%）");
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }
            errmsg = Validation.checkRangeNumber(Number(item.tax), 0, 100, "消費税（%）")
            if (errmsg){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                setSubmitDisabled(false);
                return true;
            }

            before_edate = new Date(item.datetime_end);

        });
        if (errmsg){
            return false;
        }

        //変更処理
        postApiDataWithToken('/management/settings/tax',
            {"tax_list": tmpRowData},
            cua.getToken(),
            function(res:AxiosResponse<any>){
                switch (res.status){
                    case 200:
                    setMsgArgs({type:mb.MSGBOX_TYPE_SUCCESS, text:"消費税の設定が完了しました。"});
                        break;
                    default:
                        setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"消費税の設定に失敗しました。"});
                }
                setSubmitDisabled(false);
            }
         );

    }
    
    return (
        <AdminFrame>
            <div className="contents">
                <mb.MessageBox args={msgArgs}/>
                <h2 className="settings-title-h2">消費税設定</h2>
                <div className="settings-wrap-short">
                    <div className="settings-block">
                        <h3 className="settings-title-h3">消費税設定</h3>
                        <div className="settings-form">
                            <div className="form-row-comment">
                                ※上から時系列順でご登録ください。<br/>
                                ※開始日以降、終了予定のない設定をしたい場合は、適用終了日を未入力でご登録ください。<br/>
                                　最終行のみ未入力が可能です。
                            </div>
                            <div className="form-row">
                                <div className="tbl-wrap">
                                    <table className="tbl-form js-tbl-tax margin-tb-10">
                                        <thead>
                                            <tr>
                                                <th>適用開始日</th>
                                                <th>適用終了日</th>
                                                <th>消費税（%）</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rowRef.map(function(item, idx){
                                                return (
                                                    <tr key={idx}>
                                                        <td><InputDate refElm={item.datetime_start}/></td>
                                                        <td><InputDate refElm={item.datetime_end}/></td>
                                                        <td><input type="text" className="input-size-s" ref={item.tax} maxLength={6}/></td>
                                                        <td><button className="btn-row js-btn-del-tax" onClick={function(){clickDelRow(idx)}}>
                                                                <span className="material-icons-round">clear</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <button className="btn-tertiary js-btn-add-tax btn-icon-left" onClick={clickAddRow}>
                                    <span className="icon material-icons-round md-18">add_circle_outline</span>追加
                                </button>
                            </div>
                        </div>
                    </div>
                    <button className="btn-primary margin-tb-20" onClick={clickSave} disabled={submitDisabled}>変更を保存</button>
                </div>
            </div>
        </AdminFrame>
    )

}